import React, { useCallback, useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { getCompanyDocuments } from "../../../services/firestoreServices";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../types/navigationParams";
import { useFocusEffect } from "@react-navigation/native";
import { Order, PaymentDataItem, WorkDataItem, Expense } from "./types";
import { WorkTable } from "./WorkTable";
import { PartsTable } from "./PartsTable";
import { PaymentsTable } from "./PaymentsTable";
import { DailyEarningsExpensesTable } from "./DailyEarningsExpensesTable";
import WeekNavigator from "../../../components/WeekNavigator";
import { useWeekNavigation } from "../../../hooks/useWeekNavigation";

const collectionName = "repair_orders";

type STOReportsScreenType = NativeStackScreenProps<
  RootStackParamList,
  "ReportsScreen"
>;

export const STOReportsScreen = ({}: STOReportsScreenType) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [employees, setEmployees] = useState([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const {
    currentWeek,
    weekDates,
    goToPreviousWeek,
    goToNextWeek,
    getWeekWhere,
  } = useWeekNavigation();
  const [loading, setLoading] = useState(true);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      getCompanyDocuments(collectionName, {
        where: getWeekWhere("dateCreated"),
      })
        .then((data) => {
          const onlyCompletedOrders = data.filter(
            (o: Order) => o.status === "Completed",
          );
          setOrders(onlyCompletedOrders);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          console.error(e);
        });

      getCompanyDocuments("expenses", {
        where: getWeekWhere("date"),
      })
        .then((data) => {
          setExpenses(data);
        })
        .catch((e) => {
          console.error(e);
        });
    }, [currentWeek]),
  );

  useEffect(() => {
    getCompanyDocuments("mechanics").then(setEmployees);
  }, []);

  const partsData: WorkDataItem[] = orders.flatMap((order) => {
    const date = order.dateCreated.toDate().toLocaleDateString("ru-RU");
    if (
      !order.parts ||
      !Array.isArray(order.parts) ||
      order.parts.length === 0
    ) {
      return [];
    }
    const data = order.parts.map((mechanic) => ({
      employeeId: mechanic.id,
      employeeName: mechanic.fullName,
      date,
      amount: mechanic.relatedObjectField.reduce(
        (sum: number, work) =>
          sum +
          (typeof work.price === "string" ? parseInt(work.price) : work.price),
        0,
      ),
    }));

    return data;
  });

  const paymentsData: PaymentDataItem[] = orders.map((order) => {
    const date = order.dateCreated.toDate().toLocaleDateString("ru-RU");
    const payments = Array.isArray(order.payments) ? order.payments : [];
    return {
      date,
      dateCreated: order.dateCreated.toDate(),
      cash: payments
        .filter((p) => p.paymentType === "cash")
        .reduce((sum, p) => sum + parseInt(p.paymentAmount), 0),
      kaspi: payments
        .filter((p) => p.paymentType === "kaspi")
        .reduce((sum, p) => sum + parseInt(p.paymentAmount), 0),
      halyk: payments
        .filter((p) => p.paymentType === "halyk")
        .reduce((sum, p) => sum + parseInt(p.paymentAmount), 0),
    };
  });

  const dailyEarnings: Record<string, number> = weekDates.reduce(
    (acc, date) => {
      const dateStr = date.toLocaleDateString("ru-RU");
      const dayPayments = paymentsData.filter((item) => item.date === dateStr);
      const dayTotal = dayPayments.reduce(
        (sum, item) => sum + item.cash + item.kaspi + item.halyk,
        0,
      );
      acc[dateStr] = dayTotal;
      return acc;
    },
    {} as Record<string, number>,
  );

  const dailyExpenses: Record<string, number> = weekDates.reduce(
    (acc, date) => {
      const dateStr = date.toLocaleDateString("ru-RU");
      const dayExpenses = expenses.filter(
        (expense) =>
          expense.date.toDate().toLocaleDateString("ru-RU") === dateStr,
      );
      const dayTotal = dayExpenses.reduce(
        (sum, expense) => sum + expense.amount,
        0,
      );
      acc[dateStr] = dayTotal;
      return acc;
    },
    {} as Record<string, number>,
  );

  const renderTables = () => {
    if (loading) {
      return (
        <View style={{ padding: 100 }}>
          <ActivityIndicator size="large" />
        </View>
      );
    }

    return (
      <>
        <WorkTable
          orders={orders}
          weekDates={weekDates}
          showHalfSalary={true}
        />
        <PartsTable
          data={partsData}
          weekDates={weekDates}
          employees={employees}
        />
        <PaymentsTable data={paymentsData} weekDates={weekDates} />
        <DailyEarningsExpensesTable
          weekDates={weekDates}
          dailyEarnings={dailyEarnings}
          dailyExpenses={dailyExpenses}
        />
      </>
    );
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 10 }}>
      <WeekNavigator
        weekDates={weekDates}
        onPrevious={goToPreviousWeek}
        onNext={goToNextWeek}
      />
      {renderTables()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRadius: 8,
  },
});
